import * as ents from '@attain-apps/entities'
import type * as store from '@attain-apps/fe/web/main/lib/store'

import type * as me from '../../../../me'
import type * as surveyAdmins from '../../../user-access/survey-admins'
import type { Plugin as AccountDimensionFilterPlugin } from '../account-dimension-filter/plugin'
import type { Plugin as ClusterDimensionFilterPlugin } from '../cluster-dimension-filter/plugin'
import type { Plugin as EmployeeDimensionFilterPlugin } from '../employee-dimension-filter/plugin'
import type { Plugin as OrganizationDimensionFilterPlugin } from '../organization-dimension-filter/plugin'
import type { Plugin as RoomTypeDimensionFilterPlugin } from '../room-type-dimension-filter/plugin'
import type { Plugin as SpaceDimensionFilterPlugin } from '../space-dimension-filter/plugin'

import * as handlers from './handlers'
import type { Plugin } from './plugin'

/** Selectors for the Space report configuration plugin. */
export class Selectors {
  /** Raw settings for one report, defaulting to that report's defaults. */
  public readonly getSettings

  /**
   * A report's full configuration: its stored settings, overlaid with the
   * current selections of the dimension filters that report declares and with
   * anything the current user is not entitled to turned off.
   */
  public readonly getConfiguration

  /**
   * Whether the current user is a standard user — neither a Platform Admin,
   * Tenant Admin nor Survey Admin — and so may not see Salary Information.
   */
  public readonly isStandardUser

  constructor(
    plugin: Plugin,
    settingsStorage: Plugin['settingsStorage'],
    filterPlugins: Selectors.FilterPlugins,
    meFeature: me.Feature,
    surveyAdminsEc: surveyAdmins.Collection,
  ) {
    this.isStandardUser =
      meFeature.contexts.selectors.currentUserCanManageUsers.project(
        meFeature.selectors.currentUserId.asSelector,
        surveyAdminsEc.plugins.currentListPlugin.feature.selectors.entities
          .asSelector,
        (canManageUsers, currentUserId, admins) =>
          !currentUserId ||
          (!canManageUsers &&
            !admins.some(({ userId }) => userId === currentUserId)),
      )

    // NOTE: the storage lookup is keyed by report ID but typed by the union of
    // every report's configuration, since `store.storage.lookup` can't
    // correlate the two. Its `get`/`set` only ever produce `Configuration<id>`
    // for `id`, so the assertion narrows that union back down — kept on the
    // looked-up value rather than on the whole method, so that everything
    // except this one step stays checked
    this.getSettings = <TId extends ents.extra.space.Report.Id>(id: TId) =>
      settingsStorage.selectors.getFromLookup(
        id,
      ) as store.selectors.BoundSelector<
        ents.extra.space.Report.Configuration<TId>
      >

    /** The current selection of every dimension-filter plugin. */
    const dimensionFilters =
      filterPlugins.account.selectors.deepestSelection.project(
        filterPlugins.cluster.selectors.deepestSelection.asSelector,
        filterPlugins.employee.selectors.deepestSelection.asSelector,
        filterPlugins.organization.selectors.deepestSelection.asSelector,
        filterPlugins.roomType.selectors.deepestSelection.asSelector,
        filterPlugins.space.selectors.deepestSelection.asSelector,
        (
          accountFilter,
          clusterFilter,
          employeeFilter,
          organizationFilter,
          roomTypeFilter,
          spaceFilter,
        ): handlers.DimensionFilters => ({
          accountFilter,
          clusterFilter,
          employeeFilter,
          organizationFilter,
          roomTypeFilter,
          spaceFilter,
        }),
      )

    // NOTE: `createSelectorMethod` can't carry a type parameter through, so the
    // correlation `handlers.composeDimensionFilters` already guarantees is
    // asserted back onto its result
    this.getConfiguration = plugin.feature.createSelectorMethod(
      <TId extends ents.extra.space.Report.Id>(id: TId) =>
        this.getSettings(id).project(
          dimensionFilters.asSelector,
          this.isStandardUser.asSelector,
          (settings, currentDimensionFilters, isStandardUser) => {
            const configuration = handlers.composeDimensionFilters<TId>(
              id,
              settings,
              currentDimensionFilters,
            )
            // Standard users never get Salary Information, whatever the stored
            // configuration says
            return isStandardUser &&
              ents.extra.space.Report.Configuration.WithSalaryInformation.isWithSalaryInformation(
                configuration,
              )
              ? { ...configuration, showSalaryInformation: false }
              : configuration
          },
        ),
    )
  }
}

export namespace Selectors {
  export interface FilterPlugins {
    readonly account: AccountDimensionFilterPlugin
    readonly cluster: ClusterDimensionFilterPlugin
    readonly organization: OrganizationDimensionFilterPlugin
    readonly employee: EmployeeDimensionFilterPlugin
    readonly roomType: RoomTypeDimensionFilterPlugin
    readonly space: SpaceDimensionFilterPlugin
  }
}
